import { useState, useMemo } from 'react'
import { useLocalStorage } from '@/hooks/useLocalStorage'

export type SidebarMode = 'expanded' | 'collapsed' | 'hover'

interface SidebarState {
  mode: SidebarMode
  isHovered: boolean
  isExpanded: boolean
}

interface UseSidebarStateReturn {
  state: SidebarState
  setMode: (mode: SidebarMode) => void
  setHovered: (hovered: boolean) => void
  SIDEBAR_WIDTH_COLLAPSED: number
  SIDEBAR_WIDTH_EXPANDED: number
  currentWidth: number
}

const SIDEBAR_WIDTH_COLLAPSED = 60
const SIDEBAR_WIDTH_EXPANDED = 240

export function useSidebarState(): UseSidebarStateReturn {
  const [mode, setMode] = useLocalStorage<SidebarMode>('sidebar-mode', 'expanded')
  const [isHovered, setIsHovered] = useState(false)

  const isExpanded = useMemo(() => {
    if (mode === 'expanded') return true
    if (mode === 'collapsed') return false
    return isHovered // mode === 'hover'
  }, [mode, isHovered])

  const currentWidth = isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED

  const state: SidebarState = useMemo(
    () => ({
      mode,
      isHovered,
      isExpanded,
    }),
    [mode, isHovered, isExpanded]
  )

  return {
    state,
    setMode,
    setHovered: setIsHovered,
    SIDEBAR_WIDTH_COLLAPSED,
    SIDEBAR_WIDTH_EXPANDED,
    currentWidth,
  }
}
